import type {
  Box,
  DatabasePageSpec,
  FigureSpec,
  ResolvedFigure,
  ShapeRole,
  SliceHeaderSpec,
} from './model.js';
import { assertAuthoringBoundary } from './authoring.js';
import { box, finishFigure, point, SceneBuilder, validateViewport } from './scene-builder.js';
import { compileIsolationMatrix } from './families/isolation-matrix.js';
import { compileConcurrencySequence } from './families/concurrency-sequence.js';
import { compileMemoryLayout } from './families/memory-layout.js';
import { compileQueryPlan } from './families/query-plan.js';
import { compileLatencyDistribution } from './families/latency-distribution.js';
import { compileStackFrames } from './families/stack-frames.js';
import { compileComplexityArgument } from './families/complexity-argument.js';
import { compileExecutionArtifact } from './families/execution-artifact.js';
import { compileBTree } from './families/btree.js';
import { compileQueueView } from './families/queue-view.js';
import { compileProtocolStateMachine } from './families/protocol-state-machine.js';

export function compileFigure(spec: FigureSpec): ResolvedFigure {
  assertAuthoringBoundary(spec);
  if (spec.family === 'slice-header') return compileSliceHeader(spec);
  if (spec.family === 'database-page') return compileDatabasePage(spec);
  if (spec.family === 'isolation-matrix') return compileIsolationMatrix(spec);
  if (spec.family === 'concurrency-sequence') return compileConcurrencySequence(spec);
  if (spec.family === 'memory-layout') return compileMemoryLayout(spec);
  if (spec.family === 'query-plan') return compileQueryPlan(spec);
  if (spec.family === 'latency-distribution') return compileLatencyDistribution(spec);
  if (spec.family === 'stack-frames') return compileStackFrames(spec);
  if (spec.family === 'complexity-argument') return compileComplexityArgument(spec);
  if (spec.family === 'execution-artifact') return compileExecutionArtifact(spec);
  if (spec.family === 'btree') return compileBTree(spec);
  if (spec.family === 'queue-view') return compileQueueView(spec);
  if (spec.family === 'protocol-state-machine') return compileProtocolStateMachine(spec);
  throw new Error('view compiler: family is not registered');
}

function compileSliceHeader(spec: SliceHeaderSpec): ResolvedFigure {
  validateSlice(spec);
  const b = new SceneBuilder();
  const { width: w, height: h } = spec.viewport;
  const margin = Math.max(30, w * 0.07);
  const contentW = w - margin * 2;
  const wordGap = Math.max(16, w * 0.035);
  const wordW = Math.min(126, (contentW - wordGap * 2) / 3);
  const wordH = 58;
  const headerW = wordW * 3 + wordGap * 2;
  const headerX = (w - headerW) / 2;
  const headerY = 62;
  const depth = Math.max(8, Math.min(14, w * 0.022));
  const arrayY = h * 0.53;
  const cellW = contentW / spec.cap;
  const cellH = Math.max(55, Math.min(72, h * 0.16));
  const arrayX = margin;
  const warning: string[] = [];

  const words = [
    { id: 'slice.ptr', label: 'PTR', value: spec.pointer, role: 'pointer' as const },
    { id: 'slice.len', label: 'LEN', value: String(spec.len), role: 'active' as const },
    { id: 'slice.cap', label: 'CAP', value: String(spec.cap), role: 'capacity' as const },
  ];

  words.forEach((word, index) => {
    const x = headerX + index * (wordW + wordGap);
    const bounds = box(x, headerY, wordW, wordH);
    slab(b, word.id, word.role, bounds, depth);
    b.text(`${word.id}.label`, word.id, 'annotation', point(x + 10, headerY + 18), word.label, { size: 10, mono: true, weight: 700, uppercase: true });
    b.text(`${word.id}.value`, word.id, 'structure', point(x + wordW / 2, headerY + 43), truncate(word.value, wordW < 100 ? 10 : 16), { size: word.id === 'slice.ptr' ? 13 : 18, mono: true, weight: 700, align: 'middle' });
    b.anchor(`${word.id}.bottom`, word.id, point(x + wordW / 2, headerY + wordH), 'down');
    b.hit(word.id, box(x - 3, headerY - depth - 3, wordW + depth + 6, wordH + depth + 6));
  });

  b.text('slice.caption', 'slice', 'annotation', point(headerX, 32), 'SLICE VALUE · THREE MACHINE WORDS', { size: 11, mono: true, weight: 700, uppercase: true });
  b.line('slice.explode.axis', 'slice', 'construction', point(w / 2, headerY + wordH + 22), point(w / 2, arrayY - 34), { dash: '4 6' });
  b.text('slice.explode.note', 'slice', 'annotation', point(w / 2 + 46, headerY + wordH + 31), 'describes backing array', { size: 11, mono: true, align: 'middle' });

  for (let i = 0; i < spec.cap; i += 1) {
    const id = `array.cell.${i}`;
    const x = arrayX + i * cellW;
    const value = spec.values[i] ?? null;
    const role: ShapeRole = i === spec.targetIndex ? 'active' : i >= spec.len ? 'capacity' : 'surface';
    const bounds = box(x, arrayY, cellW, cellH);
    slab(b, id, role, bounds, depth);
    b.text(`${id}.index`, id, 'annotation', point(x + cellW / 2 + depth / 2, arrayY - 6), `a[${i}]`, { size: Math.max(9, Math.min(11, cellW / 6)), mono: true, weight: i === spec.targetIndex ? 700 : 500, align: 'middle' });
    if (value !== null) {
      b.text(`${id}.value`, id, 'structure', point(x + cellW / 2, arrayY + cellH / 2 + 7), String(value), { size: Math.max(13, Math.min(18, cellW / 4)), mono: true, weight: i === spec.targetIndex ? 700 : 500, align: 'middle' });
    }
    b.text(`${id}.address`, id, 'annotation', point(x + cellW / 2, arrayY + cellH + 22), shortAddress(spec.pointerAddress, (i - spec.targetIndex) * spec.wordBytes), { size: Math.max(8, Math.min(10, cellW / 7)), mono: true, align: 'middle', weight: i === spec.targetIndex ? 700 : 500 });
    b.anchor(`${id}.top`, id, point(x + cellW / 2, arrayY - depth), 'up');
    b.anchor(`${id}.center`, id, point(x + cellW / 2, arrayY + cellH / 2));
    b.hit(id, box(x - 2, arrayY - depth - 2, cellW + depth + 4, cellH + depth + 4));
  }

  const ptrStartX = headerX + wordW / 2;
  const targetX = arrayX + (spec.targetIndex + 0.5) * cellW;
  const ptrStartY = headerY + wordH + 2;
  const ptrEndY = arrayY - depth - 5;
  b.path('relation.ptr', 'relation.pointer-target', 'pointer', `M ${r(ptrStartX)} ${r(ptrStartY)} C ${r(ptrStartX - 12)} ${r(ptrStartY + 54)}, ${r(targetX + 22)} ${r(ptrEndY - 52)}, ${r(targetX)} ${r(ptrEndY)}`, { markerEnd: true, label: 'dereference' });
  b.text('relation.ptr.label', 'relation.pointer-target', 'annotation', point((ptrStartX + targetX) / 2 + 12, (ptrStartY + ptrEndY) / 2), '*ptr', { size: 12, mono: true, weight: 700, rotate: -18 });
  b.relation('pointer-target', 'points-to', 'slice.ptr', `array.cell.${spec.targetIndex}`, 'pointer selects one addressed word');

  const bracketY = arrayY + cellH + 52;
  dimensionBracket(b, 'slice.len.extent', 'active', arrayX, arrayX + spec.len * cellW, bracketY, `len ${spec.len}`);
  dimensionBracket(b, 'slice.cap.extent', 'capacity', arrayX, arrayX + spec.cap * cellW, bracketY + 42, `cap ${spec.cap}`);
  b.relation('len-bounds', 'bounds', 'slice.len', `array.cell.${spec.len - 1}`, `len permits indices 0…${spec.len - 1}`);
  b.relation('cap-allocates', 'allocates', 'slice.cap', `array.cell.${spec.cap - 1}`, `capacity owns ${spec.cap} words`);

  const active = spec.focus === 'pointer'
    ? ['slice.ptr', `array.cell.${spec.targetIndex}`, 'relation.pointer-target']
    : spec.focus === 'extent'
      ? ['slice.len', 'slice.cap', 'slice.len.extent', 'slice.cap.extent']
      : ['slice'];
  active.push(...(spec.emphasis ?? []));

  if (cellW < 43) warning.push('Cell labels are approaching the phone legibility floor.');

  return finishFigure(spec, b, {
    title: 'A slice is three words describing one array',
    subtitle: 'The exploded view separates the value from the storage it describes.',
    state: spec.focus ?? 'overview',
    active,
    warnings: warning,
  });
}

function compileDatabasePage(spec: DatabasePageSpec): ResolvedFigure {
  validatePage(spec);
  const b = new SceneBuilder();
  const { width: w, height: h } = spec.viewport;
  const pageX = Math.max(74, w * 0.14);
  const pageW = w - pageX * 2;
  const pageY = 58;
  const pageH = h - 116;
  const depth = Math.max(9, Math.min(15, w * 0.024));
  const headerH = 44;
  const slotH = Math.max(28, Math.min(40, spec.tuples.length * 3 + 26));
  const recordBudget = pageH * 0.34;
  const recordTotal = spec.tuples.reduce((sum, tuple) => sum + tuple.bytes, 0);
  const records: Array<{ tuple: DatabasePageSpec['tuples'][number]; bounds: Box }> = [];
  let cursor = pageY + pageH;

  slab(b, 'page', 'surface', box(pageX, pageY, pageW, pageH), depth);
  b.text('page.title', 'page', 'annotation', point(pageX, 28), `${formatBytes(spec.pageBytes)} HEAP PAGE · SECTION`, { size: 11, mono: true, weight: 700, uppercase: true });
  b.hit('page', box(pageX - 4, pageY - depth - 4, pageW + depth + 8, pageH + depth + 8));

  const headerBox = box(pageX, pageY, pageW, headerH);
  b.rect('page.header', 'page.header', 'header', headerBox);
  b.text('page.header.label', 'page.header', 'structure', point(pageX + 16, pageY + 27), `PAGE HEADER · ${spec.headerBytes} B`, { size: 12, mono: true, weight: 700 });
  b.anchor('page.header.bottom', 'page.header', point(pageX + pageW / 2, pageY + headerH), 'down');
  b.hit('page.header', headerBox);

  const slotsBox = box(pageX, pageY + headerH, pageW, slotH);
  b.rect('page.slots', 'page.slots', 'slot', slotsBox);
  const slotW = Math.min(66, (pageW - 30) / spec.tuples.length);
  const slotStart = pageX + 15;
  spec.tuples.forEach((tuple, index) => {
    const x = slotStart + index * slotW;
    b.rect(`slot.${tuple.id}`, `slot.${tuple.id}`, 'slot', box(x, slotsBox.y + 8, slotW - 5, slotH - 16), { radius: 2, interactive: true });
    b.text(`slot.${tuple.id}.label`, `slot.${tuple.id}`, 'structure', point(x + (slotW - 5) / 2, slotsBox.y + slotH / 2 + 4), `${index + 1}→`, { size: 10, mono: true, weight: 700, align: 'middle' });
    b.hit(`slot.${tuple.id}`, box(x, slotsBox.y + 5, slotW - 5, slotH - 10));
  });

  [...spec.tuples].reverse().forEach((tuple) => {
    const height = Math.max(28, (tuple.bytes / recordTotal) * recordBudget);
    cursor -= height;
    const bounds = box(pageX, cursor, pageW, height);
    records.unshift({ tuple, bounds });
  });

  const freeY = slotsBox.y + slotsBox.height;
  const freeBottom = cursor;
  const freeBox = box(pageX, freeY, pageW, Math.max(20, freeBottom - freeY));
  b.rect('page.free-space', 'page.free-space', 'free-space', freeBox);
  b.text('page.free-space.label', 'page.free-space', 'annotation', point(pageX + pageW / 2, freeY + freeBox.height / 2 - 4), 'FREE SPACE', { size: 13, mono: true, weight: 700, align: 'middle', uppercase: true });
  b.text('page.free-space.motion', 'page.free-space', 'annotation', point(pageX + pageW / 2, freeY + freeBox.height / 2 + 16), 'slot directory ↓     tuples ↑', { size: 10, mono: true, align: 'middle' });
  b.anchor('page.free-space.top', 'page.free-space', point(pageX + pageW / 2, freeY), 'up');
  b.anchor('page.free-space.bottom', 'page.free-space', point(pageX + pageW / 2, freeBottom), 'down');
  b.hit('page.free-space', freeBox);

  records.forEach(({ tuple, bounds }, index) => {
    const labelSize = Math.min(12, bounds.height / 3);
    const labelChars = Math.max(10, Math.floor((bounds.width - 98) / (labelSize * 0.62)));
    b.rect(`tuple.${tuple.id}`, `tuple.${tuple.id}`, 'record', bounds, { interactive: true });
    b.text(`tuple.${tuple.id}.name`, `tuple.${tuple.id}`, 'structure', point(bounds.x + 16, bounds.y + bounds.height / 2 + 4), compactTupleLabel(tuple.label, labelChars), { size: labelSize, mono: true, weight: 700 });
    b.text(`tuple.${tuple.id}.bytes`, `tuple.${tuple.id}`, 'annotation', point(bounds.x + bounds.width - 14, bounds.y + bounds.height / 2 + 4), `${tuple.bytes} B`, { size: 10, mono: true, align: 'end' });
    b.anchor(`tuple.${tuple.id}.left`, `tuple.${tuple.id}`, point(bounds.x, bounds.y + bounds.height / 2), 'left');
    b.hit(`tuple.${tuple.id}`, bounds);
    b.path(`slot-link.${tuple.id}`, `slot-link.${tuple.id}`, 'construction', `M ${r(slotStart + index * slotW + (slotW - 5) / 2)} ${r(slotsBox.y + slotH - 7)} C ${r(pageX + pageW + 28)} ${r(slotsBox.y + slotH + 18)}, ${r(pageX + pageW + 28)} ${r(bounds.y + bounds.height / 2)}, ${r(pageX + pageW)} ${r(bounds.y + bounds.height / 2)}`, { dash: '3 5', markerEnd: true });
    b.relation(`slot-tuple-${tuple.id}`, 'locates', `slot.${tuple.id}`, `tuple.${tuple.id}`, `slot ${index + 1} locates ${tuple.label}`);
  });

  const railX = pageX - 30;
  b.line('page.offset.rail', 'page', 'dimension', point(railX, pageY), point(railX, pageY + pageH));
  const offsets = [
    { y: pageY, label: '0' },
    { y: pageY + headerH, label: String(spec.headerBytes) },
    { y: freeY, label: 'lower' },
    { y: freeBottom, label: 'upper' },
    { y: pageY + pageH, label: String(spec.pageBytes) },
  ];
  offsets.forEach(({ y, label }, index) => {
    b.line(`page.offset.${index}`, 'page', 'dimension', point(railX - 5, y), point(pageX - 7, y));
    b.text(`page.offset.${index}.label`, 'page', 'annotation', point(railX - 8, y + 4), label, { size: 9, mono: true, align: 'end', weight: label === 'lower' || label === 'upper' ? 700 : 500 });
  });

  b.text('page.section.note', 'page', 'annotation', point(pageX + pageW + 22, pageY + 18), 'CUT EDGE', { size: 9, mono: true, weight: 700, uppercase: true, rotate: 90 });
  b.relation('free-space-contract', 'separates', 'page.slots', `tuple.${spec.tuples.at(-1)?.id ?? ''}`, 'free space is the gap between two growing regions');

  const active = spec.focus === 'free-space'
    ? ['page.free-space']
    : spec.focus === 'tuple'
      ? [`tuple.${spec.focusTupleId ?? spec.tuples[1]?.id ?? spec.tuples[0]?.id}`, `slot.${spec.focusTupleId ?? spec.tuples[1]?.id ?? spec.tuples[0]?.id}`, `slot-link.${spec.focusTupleId ?? spec.tuples[1]?.id ?? spec.tuples[0]?.id}`]
      : ['page'];
  active.push(...(spec.emphasis ?? []));

  const warnings: string[] = [];
  if (freeBox.height < 68) warnings.push('Free-space label is close to its minimum readable band.');

  return finishFigure(spec, b, {
    title: 'A page grows inward from both ends',
    subtitle: 'The section exposes the free-space boundary without losing the page around it.',
    state: spec.focus ?? 'overview',
    active,
    warnings,
  });
}

function slab(builder: SceneBuilder, semanticId: string, role: ShapeRole, bounds: Box, depth: number): void {
  builder.polygon(`${semanticId}.top`, semanticId, 'depth', [
    point(bounds.x, bounds.y),
    point(bounds.x + depth, bounds.y - depth),
    point(bounds.x + bounds.width + depth, bounds.y - depth),
    point(bounds.x + bounds.width, bounds.y),
  ]);
  builder.polygon(`${semanticId}.side`, semanticId, 'depth', [
    point(bounds.x + bounds.width, bounds.y),
    point(bounds.x + bounds.width + depth, bounds.y - depth),
    point(bounds.x + bounds.width + depth, bounds.y + bounds.height - depth),
    point(bounds.x + bounds.width, bounds.y + bounds.height),
  ]);
  builder.rect(`${semanticId}.front`, semanticId, role, bounds);
}

function dimensionBracket(builder: SceneBuilder, semanticId: string, role: ShapeRole, x1: number, x2: number, y: number, label: string): void {
  builder.path(`${semanticId}.path`, semanticId, role, `M ${r(x1)} ${r(y - 10)} L ${r(x1)} ${r(y)} L ${r(x2)} ${r(y)} L ${r(x2)} ${r(y - 10)}`);
  builder.text(`${semanticId}.label`, semanticId, 'annotation', point((x1 + x2) / 2, y + 20), label, { size: 12, mono: true, weight: 700, align: 'middle' });
  builder.anchor(`${semanticId}.start`, semanticId, point(x1, y));
  builder.anchor(`${semanticId}.end`, semanticId, point(x2, y));
}

function validateSlice(spec: SliceHeaderSpec): void {
  if (spec.len < 1 || spec.cap < 1 || spec.len > spec.cap) throw new Error('slice-header: require 1 <= len <= cap');
  if (spec.values.length !== spec.cap) throw new Error('slice-header: values must contain exactly cap entries');
  if (spec.targetIndex < 0 || spec.targetIndex >= spec.len) throw new Error('slice-header: pointer target must be inside len');
  validateViewport(spec.viewport.width, spec.viewport.height);
}

function validatePage(spec: DatabasePageSpec): void {
  if (spec.pageBytes <= 0 || spec.headerBytes <= 0 || spec.slotBytes <= 0) throw new Error('database-page: byte counts must be positive');
  if (!spec.tuples.length) throw new Error('database-page: at least one tuple is required');
  if (spec.headerBytes + spec.slotBytes * spec.tuples.length + spec.tuples.reduce((sum, tuple) => sum + tuple.bytes, 0) >= spec.pageBytes) {
    throw new Error('database-page: fixture leaves no free space');
  }
  validateViewport(spec.viewport.width, spec.viewport.height);
}

function shortAddress(base: string, delta: number): string {
  const match = /^(0x[0-9a-f]+)$/i.exec(base);
  if (!match) return delta === 0 ? base : `${base}${delta >= 0 ? '+' : ''}${delta}`;
  const value = BigInt(match[1]);
  const next = value + BigInt(delta);
  return `…${next.toString(16).slice(-3)}`;
}

function truncate(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, Math.max(2, max - 3))}…${value.slice(-2)}`;
}

function compactTupleLabel(value: string, max: number): string {
  if (value.length <= max) return value;
  const email = /^[^:]+:([^@]+)@/.exec(value);
  if (email?.[1] && max <= 18) return truncate(`@${email[1]}`, max);
  if (value.startsWith('customer #') && max <= 18) return value.replace('customer ', '');
  return truncate(value, max);
}

function formatBytes(value: number): string {
  return value % 1024 === 0 ? `${value / 1024} KiB` : `${value} B`;
}

function r(value: number): number {
  return Math.round(value * 10) / 10;
}
